import { Row, Col, Skeleton, Collapse, message, Pagination } from 'antd';
import config from 'config';
import {
  CustomerOutput,
  OrderOutput,
  ProductOutput,
  WoocommerceProductCategoryOutput,
  useGetCustomerQuery,
  useGetProductsQuery,
  useGetWoocommerceCategoriesQuery,
} from 'gql/types';
import DonationDetails from 'modules/donations/components/DonationDetails';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import CustomerDetailsTable from 'modules/orders/components/CustomerDetailsTable';
import OrderInfoTable from 'modules/orders/components/OrderInfoTable';
import OrderItemsTable from 'modules/orders/components/OrderItemsTable';
import ProductDetails from 'modules/products/components/ProductDetails';
import { FC } from 'react';

import styles from './styles.module.scss';

type PropTypes = {
  order: OrderOutput;
};

const OrderDetails: FC<PropTypes> = ({ order }) => {
  const pagination = usePagination({ urlPagination: false });

  const customerQuery = useGetCustomerQuery({
    variables: {
      input: {
        id: order.customerId,
      },
    },
  });
  const productsQuery = useGetProductsQuery({
    variables: {
      input: {
        take: config.PAGINATION.PAGE_SIZE,
        skip: pagination.skip,
        filter: { orderId: order.id },
      },
    },
    onCompleted: (data) => {
      pagination.setTotal(data.getProducts.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const getWoocommerceCategoriesQuery = useGetWoocommerceCategoriesQuery({
    variables: {
      input: {},
    },
  });
  const woocommerceCategories =
    getWoocommerceCategoriesQuery.data?.getWoocommerceCategories;
  const customer = customerQuery.data?.getCustomer as CustomerOutput;
  const products = productsQuery.data?.getProducts.data as
    | ProductOutput[]
    | undefined;

  return (
    <Row gutter={[24, 0]} justify="space-between">
      <Col span={24} className={styles.orderInfoContainer}>
        <OrderInfoTable order={order} />
      </Col>
      <Col lg={10} span={24}>
        {customerQuery.loading ? (
          <Skeleton />
        ) : (
          customer && <CustomerDetailsTable customer={customer} />
        )}
      </Col>
      <Col lg={10} span={24} className={styles.orderItemsContainer}>
        <OrderItemsTable items={order.items} />
      </Col>
      <Col span={24} className={styles.productsContainer}>
        {productsQuery.loading ? (
          <Skeleton />
        ) : (
          <>
            <Collapse ghost>
              {products?.map((product) => {
                const productCategory = woocommerceCategories?.find(
                  (category) => category.id === product?.woocommerceCategoryId
                ) as WoocommerceProductCategoryOutput | undefined;

                return (
                  <Collapse.Panel
                    header={productCategory?.name}
                    key={product.id}
                  >
                    <ProductDetails
                      product={product}
                      woocommerceProductCategory={productCategory}
                    />
                    <DonationDetails donation={product.donation} />
                  </Collapse.Panel>
                );
              })}
            </Collapse>
            {pagination.config.total! > pagination.config.pageSize! && (
              <Pagination
                {...pagination.config}
                style={{ marginTop: 16, textAlign: 'right' }}
              />
            )}
          </>
        )}
      </Col>
    </Row>
  );
};

export default OrderDetails;
